import { useState } from "react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { MAPS_URL, OFFICE_ADDRESS } from "../lib/office";

const SUBJECTS = [
  "Partner agency inquiry",
  "Press / Editorial",
  "Talent development question",
  "General",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Required: name, email, message"); return; }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page" className="pt-32">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="label-overline text-zinc-500">Reach the studio</p>
          <h1 className="font-serif text-6xl md:text-7xl tracking-tighter font-light mt-6 leading-[0.9]">
            Contact<span className="italic text-zinc-600">.</span>
          </h1>
          <p className="text-zinc-600 mt-8 leading-relaxed text-lg max-w-md">
            Ege Models is a model management agency based in Rotterdam, the Netherlands. We work with clients,
            partner agencies, press and the talent we represent. For everything else, write to us.
          </p>
          <div className="mt-12 space-y-5 text-zinc-700">
            <p className="flex items-center gap-3">
              <Mail size={16}/>
              <a href="mailto:info@egemodels.com" data-testid="contact-email-link" className="link-underline">
                info@egemodels.com
              </a>
            </p>
            <p className="flex items-center gap-3">
              <Phone size={16}/>
              <a href="tel:+31627505783" data-testid="contact-phone-link" className="link-underline">
                +31 6 27505783
              </a>
            </p>
            <p className="flex items-center gap-3">
              <Phone size={16}/>
              <a
                href="https://wa.me/905350441515"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="link-underline"
              >
                +90 535 044 15 15
              </a>
              <span className="text-xs text-zinc-500">WhatsApp</span>
            </p>
            <p className="flex items-center gap-3"><MapPin size={16}/> <a href={MAPS_URL} target="_blank" rel="noreferrer" data-testid="contact-address-link" className="link-underline">{OFFICE_ADDRESS}</a></p>
          </div>

          <p className="text-xs text-zinc-500 mt-12 max-w-md leading-relaxed">
            Aspiring talent — please use the <a href="/apply" className="link-underline">Get Scouted</a> application instead.
            We review every submission individually.
          </p>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          {done ? (
            <div data-testid="contact-success" className="border border-black/10 p-12 bg-white">
              <p className="label-overline text-zinc-500">Sent</p>
              <h2 className="font-serif text-4xl mt-4">Message received.</h2>
              <p className="text-zinc-600 mt-4">A team member will reply within 48 hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-black/10 p-10 bg-white space-y-6">
              <Field label="Your name *">
                <Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
              </Field>
              <Field label="Email *">
                <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
              </Field>
              <Field label="Reason for getting in touch">
                <Select value={form.subject} onValueChange={(v) => set("subject", v)}>
                  <SelectTrigger data-testid="contact-subject" className="bg-transparent border-black/20 rounded-none">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent className="bg-white border-black/15 rounded-none">
                    {SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Message *">
                <Textarea data-testid="contact-message" rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="A short note — who you are, who you represent, what you'd like to discuss." className="bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"/>
              </Field>
              <div className="flex justify-end">
                <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-primary">
                  {submitting && <Loader2 size={14} className="animate-spin"/>} Send
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-overline text-zinc-600">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
